import { collection, deleteDoc, doc, serverTimestamp, writeBatch, Timestamp } from "firebase/firestore";
import { db } from "../firebase";
import { createLocalDoc, fireWrite } from "./offlineWrites";
import { descendantActivityIds, type ActivityNodeLike } from "./activityTree";
import { normalizeEntityType } from "../types";
import type { EntityType, InteractionEvent } from "../types";
import { deviceZone } from "./tripDisplay";
import { dayRangeContains } from "./calendarDay";

/** Entity types that conceptually contain other entities. Tapping one of
 *  these in the activity-logging flow drills into its detail page so the
 *  user can choose a child, drill further, or log directly at this entity.
 *  An event (HHN, a festival) holds its houses and stages the way a park
 *  holds rides, and a location (Disney Springs, CityWalk) its restaurants. */
export const CONTAINER_ENTITY_TYPES: ReadonlySet<EntityType> = new Set<EntityType>([
  "DESTINATION",
  "PARK",
  "EVENT",
  "HOTEL",
  "SHIP",
  "AIRPORT",
  "LOCATION",
]);

export function isContainerEntityType(type: EntityType | string | undefined | null): boolean {
  const current = normalizeEntityType(type);
  return current ? CONTAINER_ENTITY_TYPES.has(current) : false;
}

export interface CreateInteractionParams {
  userId: string;
  tripId: string;
  segmentId: string | null;
  /** The activity this one happened inside of (the park you checked into, the
   *  airport you're waiting in). Callers pass the parent's own `segmentId` as
   *  `segmentId` so the child stays filed under the same segment. */
  parentInteractionId?: string | null;
  entityId: string | null;
  name: string | null;
  customResponses?: Record<string, unknown>;
  /** Steps already taken — "Arrived" when coming back to the hotel. */
  events?: InteractionEvent[];
}

/**
 * Creates a new interaction document and returns its id. Offline-safe: the id
 * is generated client-side and the write persists to the local cache
 * immediately, so navigation to the new activity works without a connection.
 */
export function createInteractionAt(params: CreateInteractionParams): Promise<string> {
  const id = createLocalDoc(collection(db, "interactions"), {
    userId: params.userId,
    tripId: params.tripId,
    segmentId: params.segmentId,
    parentInteractionId: params.parentInteractionId ?? null,
    entityId: params.entityId,
    name: params.name,
    notes: null,
    postedWait: null,
    accessTierId: null,
    participants: [],
    people: [],
    events: params.events ?? [],
    photos: [],
    customResponses: params.customResponses ?? {},
    // The clock it was created on, which its day and times read in until its
    // own events (each carrying the zone they were logged in) take over.
    timezone: deviceZone() ?? null,
    // Client time so offline-created items have a real, sortable createdAt
    // immediately (a serverTimestamp reads as null until it syncs).
    createdAt: Timestamp.now(),
    updatedAt: serverTimestamp(),
  });
  return Promise.resolve(id);
}

/**
 * Log coming back to the hotel: the stay's next visit, with "Arrived" already
 * taken at this moment. It hangs off the segment rather than anything you were
 * in — you've left the park — and groups into the stay on its own (see
 * `utils/hotelStays`).
 */
export function startHotelVisit(params: {
  userId: string;
  tripId: string;
  segmentId: string | null;
  /** The stay's hotel, as `HotelStay` gives it: a place, or a custom name. */
  hotel: { entityId: string | null; name: string | null };
}): Promise<string> {
  const { hotel, ...rest } = params;
  return createInteractionAt({
    ...rest,
    parentInteractionId: null,
    entityId: hotel.entityId,
    // A custom hotel carries its name and type, so the visit joins its stay.
    name: hotel.entityId ? null : hotel.name,
    customResponses: hotel.entityId ? {} : { entityType: "HOTEL" },
    events: [
      {
        eventType: "MAIN_EVENT_START",
        eventTimestamp: Timestamp.now(),
        notes: null,
        customLabel: "Arrived",
        timezone: deviceZone() ?? null,
      },
    ],
  });
}

/** Build a `?tripId=...&segmentId=...&parentId=...` suffix that EntityDetail /
 *  Entities recognise as "the user is in the middle of logging an activity for
 *  this trip; surface trip-aware actions and propagate context across links".
 *  `parentId` carries the place being logged under, so drilling from a park
 *  into one of its rides still files the ride under the park check-in. */
export function buildTripContextSearch(
  tripId: string,
  segmentId: string | null | undefined,
  parentInteractionId?: string | null,
): string {
  const params = new URLSearchParams();
  params.set("tripId", tripId);
  if (segmentId) params.set("segmentId", segmentId);
  if (parentInteractionId) params.set("parentId", parentInteractionId);
  return `?${params.toString()}`;
}

interface MovableActivity extends ActivityNodeLike {
  segmentId?: string | null;
}

/**
 * Move an activity: re-file it under a different segment, a different parent
 * activity, or both — carrying everything nested beneath it along.
 *
 * A child always carries its parent's `segmentId` (see `utils/activityTree`),
 * so moving "Magic Kingdom" from the park week to the cruise day has to rewrite
 * the segment on every ride logged under it, or half the tree ends up filed in
 * a segment its parent left. One batch, so the tree is never half-moved.
 */
export function moveActivity(
  interactionId: string,
  next: { segmentId: string | null; parentInteractionId: string | null },
  allActivities: MovableActivity[],
): Promise<void> {
  const current = allActivities.find((a) => a.id === interactionId);
  const batch = writeBatch(db);
  batch.update(doc(db, "interactions", interactionId), {
    segmentId: next.segmentId,
    parentInteractionId: next.parentInteractionId,
    updatedAt: serverTimestamp(),
  });

  if ((current?.segmentId ?? null) !== next.segmentId) {
    for (const id of descendantActivityIds(allActivities, interactionId)) {
      batch.update(doc(db, "interactions", id), {
        segmentId: next.segmentId,
        updatedAt: serverTimestamp(),
      });
    }
  }

  fireWrite(batch.commit(), "move activity");
  return Promise.resolve();
}

/**
 * Delete an activity and lift its direct children up to where it sat.
 *
 * Deleting the park check-in must not take the day's rides with it: they were
 * really logged, and the nesting was only ever about where they were drawn. The
 * children re-file under whatever held the deleted activity — its own parent,
 * or the segment directly when it had none.
 */
export function deleteActivityLiftingChildren(
  interactionId: string,
  allActivities: MovableActivity[],
): Promise<void> {
  const current = allActivities.find((a) => a.id === interactionId);
  const children = allActivities.filter((a) => a.parentInteractionId === interactionId);

  if (children.length === 0) {
    fireWrite(deleteDoc(doc(db, "interactions", interactionId)), "delete interaction");
    return Promise.resolve();
  }

  const batch = writeBatch(db);
  for (const child of children) {
    batch.update(doc(db, "interactions", child.id), {
      parentInteractionId: current?.parentInteractionId ?? null,
      updatedAt: serverTimestamp(),
    });
  }
  batch.delete(doc(db, "interactions", interactionId));
  fireWrite(batch.commit(), "delete interaction");
  return Promise.resolve();
}

interface SegmentLike {
  id: string;
  parentSegmentId: string | null;
  startDate?: { toDate?: () => Date } | null;
  endDate?: { toDate?: () => Date } | null;
}

interface InteractionLike {
  segmentId: string | null;
}

/**
 * Pick a sensible default segment for a new activity logged from a trip-level
 * "+" button. Order of preference:
 *   1. The most recent interaction's segmentId — including null. The user's
 *      last "where am I filing this" choice is the strongest signal, even if
 *      that choice was "no segment".
 *   2. Only if there are no interactions yet on this trip: the deepest
 *      segment whose start/end dates contain `now`.
 *   3. null — no segment.
 *
 * `recentInteractions` is expected to be sorted with the most recent first.
 */
export function findDefaultSegmentId(
  segments: SegmentLike[],
  recentInteractions: InteractionLike[],
): string | null {
  if (recentInteractions.length > 0) {
    return recentInteractions[0]!.segmentId ?? null;
  }

  const byId = new Map(segments.map((s) => [s.id, s] as const));

  const depth = (s: SegmentLike): number => {
    let d = 0;
    let curr: SegmentLike | undefined = s;
    while (curr?.parentSegmentId) {
      const parent = byId.get(curr.parentSegmentId);
      if (!parent) break;
      d++;
      curr = parent;
    }
    return d;
  };

  // Calendar days, both ends included — comparing raw instants against a
  // date stored as midnight left the segment's last day out.
  const containing = segments.filter((s) => dayRangeContains(s.startDate, s.endDate));
  if (containing.length > 0) {
    return [...containing].sort((a, b) => depth(b) - depth(a))[0]!.id;
  }

  return null;
}
